import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { hasFinePointer } from '@/lib/motion'
import { SplitChars } from '@/components/motion/split-text'
import Img from '@/components/generals/img'
import { lookbookCredits } from '@/constants/content'

/** Campaign credits; hovering a name floats a behind-the-scenes photo beside the cursor. */
const Credits: FC = (): ReactNode => {
    const list = useRef<HTMLUListElement>(null)
    const preview = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const listEl = list.current
        const previewEl = preview.current

        if (!listEl || !previewEl || !hasFinePointer()) return

        const frames = Array.from(previewEl.querySelectorAll<HTMLElement>('[data-credit-frame]'))
        const xTo = gsap.quickTo(previewEl, 'x', { duration: 0.8, ease: 'expo.out' })
        const yTo = gsap.quickTo(previewEl, 'y', { duration: 0.8, ease: 'expo.out' })
        let active = -1

        gsap.set(previewEl, { xPercent: 10, yPercent: -50, autoAlpha: 0, scale: 0.7 })

        const onMove = (event: PointerEvent): void => {
            xTo(event.clientX)
            yTo(event.clientY)

            const row = (event.target as Element).closest<HTMLElement>('[data-credit]')
            const next = row ? Number(row.dataset.credit) : -1

            if (next < 0 || next === active) return

            active = next
            frames.forEach((frame, index) => gsap.set(frame, { zIndex: index === next ? 2 : 1 }))
            gsap.fromTo(frames[next], { clipPath: 'inset(0% 0% 100% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.7, ease: 'expo.out' })
        }

        const onEnter = (event: PointerEvent): void => {
            gsap.set(previewEl, { x: event.clientX, y: event.clientY })
            gsap.to(previewEl, { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'expo.out' })
        }

        const onLeave = (): void => {
            active = -1
            gsap.to(previewEl, { autoAlpha: 0, scale: 0.7, duration: 0.45, ease: 'expo.out' })
        }

        listEl.addEventListener('pointermove', onMove)
        listEl.addEventListener('pointerenter', onEnter)
        listEl.addEventListener('pointerleave', onLeave)

        return () => {
            listEl.removeEventListener('pointermove', onMove)
            listEl.removeEventListener('pointerenter', onEnter)
            listEl.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    return (
        <section id='credits' aria-labelledby='credits-title' className='border-t border-line py-24 gutter md:py-32'>
            <div className='flex flex-wrap items-end justify-between gap-6'>
                <h2 id='credits-title' data-reveal='chars' className='display-l uppercase'>
                    <SplitChars text='Credits' />
                </h2>

                <p data-reveal='fade' className='hidden label-mono text-ash lg:block'>
                    Hover a name
                </p>
            </div>

            <ul ref={list} className='group/credits mt-12 border-t border-line'>
                {lookbookCredits.map((credit, index) => (
                    <li
                        key={credit.code}
                        data-credit={index}
                        className='flex items-center justify-between gap-6 border-b border-line py-4 transition-opacity duration-500 group-hover/credits:opacity-40 hover:opacity-100! md:py-5'
                    >
                        <span className='flex items-baseline gap-5 md:gap-10'>
                            <span className='w-6 meta-mono text-ash'>
                                {credit.code}
                            </span>

                            <span data-reveal='fade' className='font-serif text-[clamp(34px,4.6vw,72px)] italic leading-none tracking-[-0.02em] text-bone'>
                                {credit.name}
                            </span>
                        </span>

                        <span className='flex items-center gap-4'>
                            <span className='hidden label-mono text-ash sm:inline'>
                                {credit.role}
                            </span>

                            <span className='relative block size-14 shrink-0 overflow-hidden lg:hidden'>
                                <Img src={credit.image} alt={credit.alt} sizes='56px' />
                            </span>
                        </span>
                    </li>
                ))}
            </ul>

            <div
                ref={preview}
                aria-hidden='true'
                className='pointer-events-none fixed left-0 top-0 z-30 hidden aspect-[4/5] w-[clamp(200px,18vw,300px)] overflow-hidden opacity-0 lg:block'
            >
                {lookbookCredits.map(credit => (
                    <div key={credit.code} data-credit-frame className='absolute inset-0'>
                        <Img src={credit.image} alt={credit.alt} sizes='300px' />
                    </div>
                ))}
            </div>
        </section>
    )
}

export default Credits
